import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { api } from './api/client';
import { BrandMark, Icon, type IconName } from './components/ui/Icon';
import { useAuth } from './stores/auth';

const navItems: Array<{
  to: string;
  label: string;
  icon: IconName;
  managerOnly?: boolean;
  systemOnly?: boolean;
}> = [
  { to: '/channels', label: 'Library', icon: 'channels' },
  { to: '/search', label: 'Search', icon: 'search' },
  { to: '/clips', label: 'Clips', icon: 'clips' },
  { to: '/leaderboard', label: 'Activity', icon: 'leaderboard' },
  { to: '/operations', label: 'Operations', icon: 'hard-drive', managerOnly: true },
  { to: '/workspace', label: 'Workspace', icon: 'user', managerOnly: true },
  { to: '/settings', label: 'System settings', icon: 'settings', systemOnly: true },
  { to: '/security', label: 'Security', icon: 'security' },
];

function Navigation({ onNavigate }: { onNavigate?: () => void }) {
  const { user, currentWorkspace } = useAuth();
  const isManager = Boolean(
    user?.is_superuser ||
      currentWorkspace?.role === 'owner' ||
      currentWorkspace?.role === 'admin',
  );
  const visibleItems = navItems.filter((item) =>
    (!item.systemOnly || user?.is_superuser) && (!item.managerOnly || isManager));
  return (
    <nav aria-label="Primary" className="space-y-1">
      {visibleItems.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          onClick={onNavigate}
          className={({ isActive }) =>
            clsx(
              'nav-link group',
              isActive
                ? 'bg-signal/10 text-signal shadow-[inset_0_0_0_1px_rgba(99,230,173,.08)]'
                : 'text-ink-300 hover:bg-white/[0.045] hover:text-ink-100',
            )
          }
        >
          {({ isActive }) => (
            <>
              <span
                className={clsx(
                  'nav-link-icon',
                  isActive
                    ? 'bg-signal/10 text-signal'
                    : 'bg-white/[0.025] text-ink-400 group-hover:text-ink-200',
                )}
              >
                <Icon name={item.icon} size={18} />
              </span>
              <span>{item.label}</span>
              {isActive && <span className="ml-auto h-1.5 w-1.5 rounded-full bg-signal" />}
            </>
          )}
        </NavLink>
      ))}
    </nav>
  );
}

function UserBadge() {
  const { user, currentWorkspace, logout } = useAuth();
  const navigate = useNavigate();
  const identity = user?.display_name || user?.email || 'User';
  const initials = identity
    .split(/[\s@._-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');
  const role = user?.is_superuser ? 'system admin' : currentWorkspace?.role ?? 'member';
  const secondary = user?.display_name && user.email
    ? `${user.email} · ${role}`
    : role;

  return (
    <div className="flex items-center gap-3 rounded-2xl border border-white/[0.06] bg-white/[0.025] p-2.5">
      <div className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-signal/20 to-sky-400/10 text-xs font-semibold text-signal">
        {initials || <Icon name="user" size={17} />}
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium text-ink-200">{identity}</div>
        <div className="truncate text-[0.66rem] text-ink-500">
          {secondary}
        </div>
      </div>
      <button
        type="button"
        className="icon-button min-h-9 min-w-9"
        aria-label="Sign out"
        title="Sign out"
        onClick={async () => {
          await logout();
          navigate('/login');
        }}
      >
        <Icon name="logout" size={17} />
      </button>
    </div>
  );
}

export function App() {
  const { currentWorkspace, user } = useAuth();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const workspaceId = currentWorkspace?.workspace_id;
  const isManager = Boolean(
    user?.is_superuser ||
      currentWorkspace?.role === 'owner' ||
      currentWorkspace?.role === 'admin',
  );
  const operator = useQuery<{
    queue_available: boolean;
    failed_entities: number;
  }>({
    queryKey: ['operator-overview'],
    queryFn: () => api('/operator/overview'),
    enabled: isManager && !!workspaceId,
    refetchInterval: 30_000,
    retry: false,
  });

  useEffect(() => setMobileMenuOpen(false), [location.pathname]);

  // nginx protects every /media request with a workspace-scoped HttpOnly
  // cookie. Refresh it during long VOD sessions so later seeks/range requests
  // and live HLS segments keep working after the original cookie expires.
  useEffect(() => {
    if (!workspaceId) return;
    const refreshMediaSession = () => {
      if (document.visibilityState === 'hidden') return;
      void api<void>('/media/session', { method: 'POST' }).catch(() => undefined);
    };
    refreshMediaSession();
    const timer = window.setInterval(refreshMediaSession, 10 * 60 * 1000);
    window.addEventListener('pageshow', refreshMediaSession);
    window.addEventListener('online', refreshMediaSession);
    document.addEventListener('visibilitychange', refreshMediaSession);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('pageshow', refreshMediaSession);
      window.removeEventListener('online', refreshMediaSession);
      document.removeEventListener('visibilitychange', refreshMediaSession);
    };
  }, [workspaceId]);

  return (
    <div className="vodvault-app-shell min-h-dvh bg-transparent md:flex md:h-dvh md:min-h-0">
      <a href="#main-content" className="skip-link">Skip to content</a>

      <aside
        className="vodvault-app-sidebar relative hidden w-[17rem] shrink-0 overflow-hidden border-r border-white/[0.06] bg-ink-850/85 md:flex md:flex-col"
        data-app-chrome="sidebar"
      >
        <div className="pointer-events-none absolute -left-20 -top-24 h-64 w-64 rounded-full bg-signal/[0.055] blur-3xl" />
        <NavLink to="/channels" className="relative flex items-center gap-3 px-5 pb-5 pt-6">
          <BrandMark />
          <div>
            <div className="text-[0.95rem] font-bold tracking-[0.16em] text-ink-100">VODVAULT</div>
            <div className="mt-0.5 text-[0.65rem] text-ink-500">Private stream archive</div>
          </div>
        </NavLink>

        <div className="relative mx-3 mb-5 rounded-2xl border border-white/[0.06] bg-gradient-to-br from-white/[0.045] to-transparent px-3.5 py-3">
          <div className="text-[0.62rem] font-semibold uppercase tracking-[0.16em] text-ink-500">
            Workspace
          </div>
          <div className="mt-1 truncate text-sm font-medium text-ink-200">
            {currentWorkspace?.workspace_name ?? currentWorkspace?.workspace_slug ?? '—'}
          </div>
        </div>

        <div className="relative flex-1 px-3">
          <div className="mb-2 px-3 text-[0.61rem] font-semibold uppercase tracking-[0.18em] text-ink-500">
            Browse
          </div>
          <Navigation />
        </div>

        <div className="relative p-3"><UserBadge /></div>
      </aside>

      <div className="vodvault-app-content min-w-0 flex-1 md:flex md:min-h-0 md:flex-col">
        <header
          className="vodvault-mobile-header sticky top-0 z-40 flex min-h-16 items-center gap-3 border-b border-white/[0.06] bg-ink-900/80 px-4 backdrop-blur-xl md:hidden"
          data-app-chrome="mobile-header"
        >
          <BrandMark className="h-9 w-9 rounded-lg" />
          <div className="min-w-0 flex-1">
            <div className="text-xs font-bold tracking-[0.14em] text-ink-100">VODVAULT</div>
            <div className="truncate text-[0.64rem] text-ink-500">
              {currentWorkspace?.workspace_name ?? currentWorkspace?.workspace_slug ?? '—'}
            </div>
          </div>
          <button
            type="button"
            className="icon-button"
            aria-label={mobileMenuOpen ? 'Close navigation' : 'Open navigation'}
            aria-expanded={mobileMenuOpen}
            aria-controls="mobile-navigation"
            onClick={() => setMobileMenuOpen((open) => !open)}
          >
            <Icon name={mobileMenuOpen ? 'close' : 'menu'} size={21} />
          </button>
        </header>

        {mobileMenuOpen && (
          <div className="fixed inset-0 top-16 z-30 bg-black/55 backdrop-blur-sm md:hidden">
            <div
              id="mobile-navigation"
              className="animate-fade-up border-b border-white/[0.07] bg-ink-850/95 p-3 shadow-float"
            >
              <Navigation onNavigate={() => setMobileMenuOpen(false)} />
              <div className="mt-3"><UserBadge /></div>
            </div>
          </div>
        )}

        <main
          id="main-content"
          tabIndex={-1}
          className="relative min-w-0 flex-1 md:min-h-0 md:overflow-auto"
        >
          <div className="pointer-events-none fixed inset-y-0 right-0 -z-0 w-1/3 bg-gradient-to-l from-sky-400/[0.018] to-transparent" />
          <div className="relative z-10">
            {location.pathname !== '/operations' && operator.data &&
              (!operator.data.queue_available || operator.data.failed_entities > 0) && (
                <div className="mx-4 mt-4 flex flex-col gap-2 rounded-xl border border-amber-300/20 bg-amber-300/[0.055] px-4 py-3 text-sm text-amber-100 sm:mx-6 sm:flex-row sm:items-center lg:mx-8">
                  <div className="min-w-0 flex-1">
                    {!operator.data.queue_available
                      ? 'The worker queue is unavailable.'
                      : `${operator.data.failed_entities} media ${operator.data.failed_entities === 1 ? 'job needs' : 'jobs need'} attention.`}
                  </div>
                  <Link className="btn-quiet min-h-8 shrink-0 px-2 py-1 text-xs text-amber-100" to="/operations">
                    Open operations
                  </Link>
                </div>
              )}
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
